import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ErrorLike, OperationVariables } from '@apollo/client';
import classNames from 'classnames';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import isAbortError from '@lib/utils/isAbortError';
import styles from './PaginatedList.module.scss';

export interface PaginatedListMetadata {
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

/**
 * Deliberately not `useLazyQuery.ExecFunction` (`useXSearchListLazyQuery`'s tuple
 * function): that hook creates a React-managed `ObservableQuery` subscription
 * per component instance via `useSyncExternalStore`, which Apollo Client 4
 * can tear down and resubscribe within the same commit when several sibling
 * instances mount together (e.g. one per table row) - aborting the in-flight
 * `BatchHttpLink` fetch they share. Pass a plain `client.query(...)` wrapper
 * (via `useApolloClient`) instead, which has no such subscription lifecycle.
 */
export type PaginatedListQueryFn<
  TData,
  TVariables extends OperationVariables,
> = (options: { variables: TVariables }) => Promise<{
  data?: TData;
  error?: ErrorLike;
}>;

export interface PaginatedListProps<
  TItem,
  TData,
  TVariables extends OperationVariables,
> {
  query: PaginatedListQueryFn<TData, TVariables>;
  /** Base variables (filters/sorts) - `page`/`limit` are added internally per page. */
  variables: TVariables;
  pageSize?: number;
  getCollection: (data: TData | undefined) => TItem[];
  getMetadata: (data: TData | undefined) => PaginatedListMetadata | undefined;
  getItemKey: (item: TItem) => string;
  renderItem: (item: TItem) => React.ReactNode;
  /**
   * `true` (default): items load inline, with a "show more" link to fetch additional pages.
   * `false`: chips-preview mode - fetches only `chipsPreviewCount` items and renders them
   * plus a "+N" counter chip. The counter chip calls `onShowAllClick`/`showAllUrl` to view the
   * full list; `renderItem` keeps whatever click behavior it normally has (e.g. navigating to
   * that item), since only the counter chip is the "view all" trigger.
   */
  useShowMore?: boolean;
  /** Chips-preview mode only. Number of items to render before the counter chip. Default 1. */
  chipsPreviewCount?: number;
  /** Chips-preview mode only. Called when the counter chip is clicked to view the full list. */
  onShowAllClick?: () => void;
  /**
   * Chips-preview mode only. Route to navigate to when the counter chip is clicked to view the
   * full list. Takes precedence over `onShowAllClick` when both are provided.
   */
  showAllUrl?: string;
  /** Chips-preview mode only. Custom render for the zero-items case (defaults to `-`). */
  renderEmptyPreview?: (onShowAllClick?: () => void) => React.ReactNode;
  /** Called whenever fetched metadata changes - lets a parent read `totalCount` without a second query. */
  onMetadataChange?: (metadata: PaginatedListMetadata | undefined) => void;
}

function PaginatedList<TItem, TData, TVariables extends OperationVariables>({
  query,
  variables,
  pageSize = 3,
  getCollection,
  getMetadata,
  getItemKey,
  renderItem,
  useShowMore = true,
  chipsPreviewCount = 1,
  onShowAllClick,
  showAllUrl,
  renderEmptyPreview,
  onMetadataChange,
}: PaginatedListProps<TItem, TData, TVariables>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<TItem[]>([]);
  const [metadata, setMetadata] = useState<PaginatedListMetadata>();
  const [firstLoading, setFirstLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorLike>();
  const fetchIdRef = useRef(0);

  const limit = useShowMore ? pageSize : chipsPreviewCount;

  const loadPage = async (page: number, retriesLeft = 2) => {
    const fetchId = fetchIdRef.current + 1;
    fetchIdRef.current = fetchId;
    setLoading(true);
    setError(undefined);
    try {
      const result = await query({
        variables: { ...variables, page, limit } as TVariables,
      });
      if (fetchId !== fetchIdRef.current) return;
      if (result.error) {
        setError(result.error);
        return;
      }
      const newItems = getCollection(result.data);
      setItems((prevItems) =>
        page === 1 ? newItems : [...prevItems, ...newItems],
      );
      const newMetadata = getMetadata(result.data);
      setMetadata(newMetadata);
      onMetadataChange?.(newMetadata);
    } catch (e) {
      if (fetchId !== fetchIdRef.current) return;
      // Defensive: retry once transparently on an aborted fetch (e.g. a
      // dropped connection) instead of surfacing a false "failed to load".
      if (isAbortError(e) && retriesLeft > 0) {
        loadPage(page, retriesLeft - 1);
        return;
      }
      setError(e as ErrorLike);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
        setFirstLoading(false);
      }
    }
  };

  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    if (metadata && metadata.currentPage < metadata.totalPages && !loading) {
      loadPage(metadata.currentPage + 1);
    }
  };

  if (firstLoading) {
    return <CircleLoader size={16} padding={0} />;
  }

  if (error) {
    return <span className={styles.error}>{t('failed-to-load')}</span>;
  }

  if (!useShowMore) {
    const resolvedOnShowAllClick =
      showAllUrl || onShowAllClick
        ? () => (showAllUrl ? navigate(showAllUrl) : onShowAllClick?.())
        : undefined;

    if (!items.length) {
      return renderEmptyPreview ? (
        <>{renderEmptyPreview(resolvedOnShowAllClick)}</>
      ) : (
        <span>-</span>
      );
    }

    const remainingCount =
      (metadata?.totalCount ?? items.length) - items.length;

    return (
      <div className={classNames(styles.list, styles.previewList)}>
        {items.map((item) => (
          <React.Fragment key={getItemKey(item)}>
            {renderItem(item)}
          </React.Fragment>
        ))}
        {remainingCount > 0 && (
          <Chip
            className={styles.counterChip}
            type={ChipTypes.chipGray}
            label={`+${remainingCount}`}
            onClick={resolvedOnShowAllClick}
          />
        )}
      </div>
    );
  }

  if (!items.length) return <span>-</span>;

  const hasMore = !!metadata && metadata.currentPage < metadata.totalPages;

  return (
    <div className={styles.root}>
      <div className={styles.list}>
        {items.map((item) => (
          <React.Fragment key={getItemKey(item)}>
            {renderItem(item)}
          </React.Fragment>
        ))}
      </div>
      {hasMore && (
        <LinkComponent
          size={LinkSizes.small}
          type={LinkTypes.secondaryGray}
          onClick={handleLoadMore}
          disabled={loading}
          data-test-id="paginated-list__show-more"
        >
          {t('show-more')}
        </LinkComponent>
      )}
    </div>
  );
}

export default memo(PaginatedList) as <
  TItem,
  TData,
  TVariables extends OperationVariables,
>(
  props: PaginatedListProps<TItem, TData, TVariables>,
) => React.ReactElement;
